import { useMemo, useState } from 'react'
import { PanelHeader } from 'Components/common/TaskPanelHeader/TaskPanelHeader'
import AiAssistanceIcon from 'Assets/icons/ai-assistance.svg'
import AiAssistanceGenerate from 'Assets/icons/ai-assistance-generate.svg'
import NewChatIcon from 'Assets/icons/new-chat.svg'
import ClearChatIcon from 'Assets/icons/clear-chat.svg'
import { AIAssistanceBody } from './AIAssistanceBody'
import { ConversationMessageType } from 'Types/AIAssistance.types'
import styles from './AIAssistance.module.less'
import { ContainerType, TaskConfigType, TaskPromptType } from 'Types/Tasks.types'
import { isEmpty } from 'lodash'

export type AIAssistanceProps = {
    getConversation: () => ConversationMessageType[]
    onPostMessage: (message: string) => Promise<void>
    onClearChat: () => void
    onGenerate: () => void
    onNewContext: () => void
    taskConfig: TaskConfigType<ContainerType, unknown>
    getContextId: () => string
    getTaskPrompt: () => TaskPromptType | undefined
}

export const AIAssistance: React.FC<AIAssistanceProps> = (props: AIAssistanceProps) => {
    const [input, setInput] = useState('')
    const { onGenerate, onClearChat, onNewContext, getTaskPrompt } = props
    const taskPrompt = getTaskPrompt()

    const actionButtons = useMemo(() => {
        /*
         * Only show the Generate from Prompt option if the content is a string
         */
        const buttons =
            typeof taskPrompt?.value === 'string' && !isEmpty(taskPrompt?.value)
                ? [
                      {
                          action: () => {
                              onGenerate()
                          },
                          title: 'Generate a response for the task’s prompt',
                          icon: AiAssistanceGenerate,
                      },
                  ]
                : []
        buttons.push(
            ...[
                {
                    action: () => {
                        onClearChat()
                    },
                    title: 'Clear Chat',
                    icon: ClearChatIcon,
                },
                {
                    action: () => {
                        onNewContext()
                    },
                    title: 'Start a new conversation, without clearing the chat screen.',
                    icon: NewChatIcon,
                },
            ]
        )
        return buttons
    }, [onGenerate, onClearChat, onNewContext, taskPrompt])

    const onSubmitButton = () => {
        setInput('')
        return props.onPostMessage(input)
    }

    return (
        <div className={styles['ai-assistance-container']} data-testid="AI-Assistance">
            <PanelHeader
                logo={AiAssistanceIcon}
                title={'AI Assistance'}
                actionButtons={actionButtons}
            />
            <AIAssistanceBody
                input={input}
                contextId={props.getContextId()}
                taskConfig={props.taskConfig}
                updateInput={setInput}
                conversations={props.getConversation()}
                onSubmitButton={onSubmitButton}
            />
        </div>
    )
}
